// SpecRef: ARCH-003
// See: docs/decisions/ARCH-003-wind-heatmap-color-themes.md#themes
// Single source of truth for the active wind heatmap color theme.

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

import { SYSTEM_THEME_IDS, WIND_COLOR_THEMES } from '../colorThemes';

import type { SystemThemeId, WindColorTheme } from '../types';

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// 🎨 CHANGE THE DEFAULT THEME HERE
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
export const DEFAULT_WIND_THEME_ID: SystemThemeId = 'cool_spectral';
export const DEFAULT_WIND_THEME_OPACITY = 0.45;
export const DEFAULT_WIND_THEME_THRESHOLD = 0.25;
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

// ── localStorage persistence ──────────────────────────────────────────────

const LS_KEY = 'windTheme';

interface PersistedState {
  themeId: SystemThemeId;
  opacity: number;
  threshold: number;
}

const DEFAULT_STATE: PersistedState = {
  themeId: DEFAULT_WIND_THEME_ID,
  opacity: DEFAULT_WIND_THEME_OPACITY,
  threshold: DEFAULT_WIND_THEME_THRESHOLD,
};

const isValidThemeId = (id: string): id is SystemThemeId =>
  (SYSTEM_THEME_IDS as string[]).includes(id);

const loadState = (): PersistedState => {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) {
      return DEFAULT_STATE;
    }
    const parsed = JSON.parse(raw) as Partial<PersistedState>;
    return {
      themeId:
        parsed.themeId && isValidThemeId(parsed.themeId)
          ? parsed.themeId
          : DEFAULT_STATE.themeId,
      opacity:
        typeof parsed.opacity === 'number'
          ? Math.max(0, Math.min(1, parsed.opacity))
          : DEFAULT_STATE.opacity,
      threshold:
        typeof parsed.threshold === 'number'
          ? Math.max(0, Math.min(1, parsed.threshold))
          : DEFAULT_STATE.threshold,
    };
  } catch {
    return DEFAULT_STATE;
  }
};

const saveState = (state: PersistedState) => {
  try {
    localStorage.setItem(LS_KEY, JSON.stringify(state));
  } catch {
    // quota exceeded — silently ignore
  }
};

// ── Resolve theme by ID ───────────────────────────────────────────────────

const resolveTheme = (id: SystemThemeId): WindColorTheme =>
  WIND_COLOR_THEMES[id] ?? WIND_COLOR_THEMES[DEFAULT_WIND_THEME_ID];

// ── Context ───────────────────────────────────────────────────────────────

interface WindThemeContextValue {
  theme: WindColorTheme;
  themeId: SystemThemeId;
  /** Global opacity multiplier [0..1]. */
  opacity: number;
  /** Opacity threshold [0..1] — wind speeds below this normalized value are fully transparent. */
  threshold: number;
  setThemeId: (id: SystemThemeId) => void;
  setOpacity: (value: number) => void;
  setThreshold: (value: number) => void;
}

const WindThemeContext = createContext<WindThemeContextValue>({
  theme: WIND_COLOR_THEMES[DEFAULT_WIND_THEME_ID],
  themeId: DEFAULT_WIND_THEME_ID,
  opacity: 1,
  threshold: 0,
  setThemeId: () => {},
  setOpacity: () => {},
  setThreshold: () => {},
});

export const useWindTheme = (): WindThemeContextValue =>
  useContext(WindThemeContext);

interface WindThemeProviderProps {
  children: ReactNode;
}

export const WindThemeProvider = ({ children }: WindThemeProviderProps) => {
  const [state, setState] = useState<PersistedState>(loadState);

  const setThemeId = useCallback((id: SystemThemeId) => {
    setState(prev => {
      const next = { ...prev, themeId: id };
      saveState(next);
      return next;
    });
  }, []);

  const setOpacity = useCallback((value: number) => {
    setState(prev => {
      const next = { ...prev, opacity: Math.max(0, Math.min(1, value)) };
      saveState(next);
      return next;
    });
  }, []);

  const setThreshold = useCallback((value: number) => {
    setState(prev => {
      const next = { ...prev, threshold: Math.max(0, Math.min(1, value)) };
      saveState(next);
      return next;
    });
  }, []);

  const value = useMemo<WindThemeContextValue>(
    () => ({
      theme: resolveTheme(state.themeId),
      themeId: state.themeId,
      opacity: state.opacity,
      threshold: state.threshold,
      setThemeId,
      setOpacity,
      setThreshold,
    }),
    [state, setThemeId, setOpacity, setThreshold],
  );

  return (
    <WindThemeContext.Provider value={value}>
      {children}
    </WindThemeContext.Provider>
  );
};
